import { depositAmount } from './needs.js';
import { isPositiveAmount, MONEY_EPSILON, toCents } from './money.js';
/**
 * Where a line's money stands: nothing owed and nothing paid is paid (a vendor
 * comped at $0), more paid than the price is overpaid.
 */
export function moneyStateOf(price, paid) {
    const balance = (toCents(price) - toCents(paid)) / 100;
    if (balance < -MONEY_EPSILON)
        return 'overpaid';
    if (!isPositiveAmount(balance))
        return 'paid';
    return isPositiveAmount(paid) ? 'partly_paid' : 'unpaid';
}
/** The schedule in the order it falls due — by day, then the order it was written. */
export function orderInstallments(rows) {
    return [...rows].sort((a, b) => a.dueDate.localeCompare(b.dueDate) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}
/**
 * A line's money with its payments applied to its installments oldest first —
 * derived, never stored. A line with no installments reads its single due date
 * (which may be none) as a one-installment schedule of the whole price, so
 * nothing written before schedules existed reads differently.
 *
 * @param today the office day, `YYYY-MM-DD`: an installment due before it with
 * something left is overdue.
 */
export function lineMoney(input) {
    const { price, paid, today } = input;
    const schedule = input.installments && input.installments.length
        ? orderInstallments(input.installments).map((i) => ({
            id: i.id ?? null,
            label: i.label ?? null,
            amount: i.amount,
            dueDate: i.dueDate,
        }))
        : [{ id: null, label: null, amount: price, dueDate: input.dueDate ?? null }];
    let unapplied = Math.max(0, toCents(paid));
    const installments = schedule.map((row) => {
        const amount = toCents(row.amount);
        const applied = Math.min(amount, unapplied);
        unapplied -= applied;
        const remaining = amount - applied;
        let state;
        if (remaining <= 0)
            state = 'paid';
        else if (row.dueDate && row.dueDate < today)
            state = 'overdue';
        else
            state = applied > 0 ? 'partly_paid' : 'due';
        return {
            id: row.id,
            label: row.label,
            amount: amount / 100,
            dueDate: row.dueDate,
            paid: applied / 100,
            remaining: remaining / 100,
            state,
        };
    });
    const overdueCents = installments
        .filter((i) => i.state === 'overdue')
        .reduce((sum, i) => sum + toCents(i.remaining), 0);
    const next = installments.find((i) => i.state !== 'paid');
    return {
        state: moneyStateOf(price, paid),
        overdue: overdueCents > 0,
        overdueAmount: overdueCents / 100,
        nextDue: next ? { date: next.dueDate, amount: next.remaining, label: next.label } : null,
        installments,
    };
}
/**
 * Why a schedule cannot be saved against a price, in words the office can act
 * on — or null. An empty schedule is fine: the line falls back to its single
 * due date.
 */
export function scheduleProblem(price, rows) {
    if (!rows.length)
        return null;
    for (const row of rows) {
        if (!(row.amount > MONEY_EPSILON))
            return 'Every installment needs an amount above $0';
        if (!row.dueDate)
            return 'Every installment needs a due date';
    }
    const diff = toCents(price) - rows.reduce((sum, r) => sum + toCents(r.amount), 0);
    if (diff > 0)
        return `The installments are $${(diff / 100).toFixed(2)} short of the price`;
    if (diff < 0)
        return `The installments are $${(-diff / 100).toFixed(2)} over the price`;
    return null;
}
/** What is left of the price after the installments written so far, in dollars. */
export function scheduleRemainder(price, rows) {
    return (toCents(price) - rows.reduce((sum, r) => sum + toCents(r.amount || 0), 0)) / 100;
}
/**
 * A deposit and the balance: the deposit as a percent of the price or an
 * amount, the balance whatever is left. A deposit of the whole price (or more)
 * is one installment; a deposit of nothing is the balance alone.
 */
export function depositAndBalance(price, deposit, depositDue, balanceDue) {
    const priceCents = toCents(price);
    const depositCents = Math.min(priceCents, deposit.amount !== undefined
        ? toCents(deposit.amount)
        : Math.round((priceCents * (deposit.percent ?? 0)) / 100));
    const rows = [];
    if (depositCents > 0)
        rows.push({ label: 'Deposit', amount: depositCents / 100, dueDate: depositDue });
    if (priceCents - depositCents > 0) {
        rows.push({ label: 'Balance', amount: (priceCents - depositCents) / 100, dueDate: balanceDue });
    }
    return rows;
}
/**
 * The price split into equal installments on the days given, the odd cents on
 * the last so the sum is the price exactly.
 */
export function splitEvenly(price, dueDates) {
    const n = dueDates.length;
    if (!n)
        return [];
    const priceCents = toCents(price);
    const each = Math.floor(priceCents / n);
    return dueDates.map((dueDate, i) => ({
        label: `Payment ${i + 1} of ${n}`,
        amount: (i === n - 1 ? priceCents - each * (n - 1) : each) / 100,
        dueDate,
    }));
}
/**
 * The schedule an accepted quote's terms give: its deposit on the day accept
 * resolved (`dueDate`), the balance on `balanceDue` (the event's day, or the
 * line's own due date). Terms that name no day, or no terms at all, give none —
 * the line keeps its single due date.
 */
export function scheduleFromTerms(price, terms, balanceDue) {
    if (!terms)
        return [];
    const depositDue = terms.dueDate ?? (terms.due !== 'signing' ? terms.due : null);
    if (!depositDue)
        return [];
    const deposit = depositAmount(terms, price);
    if (deposit === null)
        return [];
    return depositAndBalance(price, { amount: deposit }, depositDue, balanceDue ?? depositDue);
}
/**
 * What a line has overdue and what falls due from today through the window's
 * last day (`addDaysToKey(today, CASH_DUE_WINDOW_DAYS)`) — the two cash figures
 * the Plan's strip and the Expenses summary total, read off one `lineMoney`.
 */
export function cashFigures(money, windowEnd) {
    let dueSoon = 0;
    for (const i of money.installments) {
        if (i.state === 'paid' || i.state === 'overdue' || !i.dueDate)
            continue;
        if (i.dueDate <= windowEnd)
            dueSoon += toCents(i.remaining);
    }
    return { overdue: money.overdueAmount, dueSoon: dueSoon / 100 };
}
//# sourceMappingURL=installments.js.map